import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { MegaphoneIcon as Megaphone, TrashIcon as Trash, PencilSimpleIcon as PencilSimple, PaperPlaneTiltIcon as PaperPlaneTilt, ChatCircleIcon as ChatCircle } from "@phosphor-icons/react";

const EMPTY = { title: "", tag: "", body: "", starts_at: "", ends_at: "" };

function statusOf(ov) {
  const now = new Date().toISOString();
  if (ov.ends_at < now) return "past";
  if (ov.starts_at > now) return "upcoming";
  return "active";
}

const STATUS_STYLES = {
  active: "bg-[#EAF6EC] text-[#2F7A3D] border-[#BCE0C4]",
  upcoming: "bg-[#FFF7E0] text-[#8A6D00] border-[#EAD98A]",
  past: "bg-[#F2F2F2] text-[#6B6B6B] border-[#E0E0E0]",
};

export default function AdminFeedPrompts() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const load = () =>
    api.get("/admin/posts/prompts").then((r) => setData(r.data)).catch(() => setData({}));

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || !form.tag || !form.body || !form.starts_at || !form.ends_at) {
      toast.error("Fill in every field, including both dates.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        starts_at: new Date(form.starts_at + "T00:00:00Z").toISOString(),
        ends_at: new Date(form.ends_at + "T23:59:59Z").toISOString(),
      };
      if (editingId) {
        await api.patch(`/admin/posts/prompts/${editingId}`, payload);
        toast.success("Override updated");
      } else {
        await api.post("/admin/posts/prompts", payload);
        toast.success("Override scheduled");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the override.");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (ov) => {
    setEditingId(ov.id);
    setForm({
      title: ov.title, tag: ov.tag, body: ov.body,
      starts_at: ov.starts_at.slice(0, 10),
      ends_at: ov.ends_at.slice(0, 10),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (ov) => {
    if (!window.confirm(`Delete "${ov.title}"?`)) return;
    try {
      await api.delete(`/admin/posts/prompts/${ov.id}`);
      toast.success("Override deleted");
      load();
    } catch {
      toast.error("Couldn't delete the override.");
    }
  };

  const pushNow = async () => {
    const cur = data?.current;
    if (!window.confirm(
      `Send "${cur?.title}" (${cur?.tag}) as a push notification + inbox message to all members now?`
    )) return;
    setPushBusy(true);
    try {
      const { data: r } = await api.post("/admin/posts/prompts/push-now");
      toast.success(`Push queued for ${r.queued} members`);
      setTimeout(load, 4000);
    } catch {
      toast.error("Couldn't queue the push.");
    } finally {
      setPushBusy(false);
    }
  };

  const cur = data?.current;
  const overrides = data?.overrides || [];
  const rotation = data?.rotation || [];
  const rotationCustomized = data?.rotation_customized;
  const lastPush = data?.last_push;

  const [rotEditIdx, setRotEditIdx] = useState(null);
  const [rotForm, setRotForm] = useState({ title: "", tag: "", body: "" });
  const [rotBusy, setRotBusy] = useState(false);

  const startRotEdit = (i, p) => {
    setRotEditIdx(i);
    setRotForm({ title: p.title, tag: p.tag, body: p.body });
  };

  const saveRotEdit = async () => {
    if (!rotForm.title || !rotForm.tag || !rotForm.body) {
      toast.error("Title, hashtag, and text are all required.");
      return;
    }
    setRotBusy(true);
    try {
      await api.put(`/admin/posts/prompts/rotation/${rotEditIdx}`, rotForm);
      toast.success("Rotation prompt updated");
      setRotEditIdx(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the prompt.");
    } finally {
      setRotBusy(false);
    }
  };

  const resetRotation = async () => {
    if (!window.confirm("Reset all 12 rotation prompts back to the defaults?")) return;
    try {
      await api.post("/admin/posts/prompts/rotation/reset");
      toast.success("Rotation reset to defaults");
      setRotEditIdx(null);
      load();
    } catch {
      toast.error("Couldn't reset the rotation.");
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          The Dog's Voice
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Feed prompts
        </h1>
        <p className="text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          The feed rotates through 12 default weekly prompts, all written in
          the dog's voice — keep that perspective when editing or scheduling
          so members post as their dogs. An override for special events or
          launches replaces the rotation everywhere while it's live,
          including the Monday push.
        </p>

        {/* Current prompt + push now */}
        {cur && (
          <div className="mt-8 rounded-xl border border-[#C9A227]/40 bg-[#FFFBEF] p-5" data-testid="current-prompt-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#8A6D00]">
                  Live right now {cur.override && "· OVERRIDE"}
                </p>
                <p className="font-bold text-ink text-lg mt-1">{cur.title} <span className="font-normal text-[#8A6D00]">{cur.tag}</span></p>
                <p className="text-[13px] text-[#6B6B6B] mt-1">{cur.body}</p>
                {cur.override && cur.ends_at && (
                  <p className="text-[11px] text-[#8A6D00] mt-1">
                    Runs until {new Date(cur.ends_at).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="text-right">
                <Button
                  onClick={pushNow}
                  disabled={pushBusy}
                  className="rounded-full bg-ink text-white hover:bg-[#333] h-10 px-5"
                  data-testid="push-prompt-now-btn"
                >
                  <PaperPlaneTilt size={16} weight="bold" className="mr-2" />
                  {pushBusy ? "Queuing…" : "Send push now"}
                </Button>
                {lastPush && (
                  <p className="text-[11px] text-[#6B6B6B] mt-2" data-testid="last-push-info">
                    Last push: {new Date(lastPush.started_at).toLocaleString()}
                    {" "}· {lastPush.done ? `${lastPush.sent} sent` : "sending…"}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create / edit form */}
        <div className="mt-8 rounded-xl border border-[#E5E5E5] bg-white p-6" data-testid="override-form">
          <h2 className="font-spectral text-lg text-ink flex items-center gap-2">
            <Megaphone size={20} weight="duotone" className="text-gold-dark" />
            {editingId ? "Edit override" : "Schedule a special-event prompt"}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label htmlFor="ov-title" className="text-xs">Title</Label>
              <Input id="ov-title" value={form.title} onChange={set("title")}
                placeholder="Launch Week Celebration" data-testid="override-title-input" />
            </div>
            <div>
              <Label htmlFor="ov-tag" className="text-xs">Hashtag</Label>
              <Input id="ov-tag" value={form.tag} onChange={set("tag")}
                placeholder="#LaunchWeek" data-testid="override-tag-input" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ov-body" className="text-xs">Prompt text (what members see)</Label>
              <Textarea id="ov-body" value={form.body} onChange={set("body")} rows={2}
                placeholder="The humans finally launched us on the App Store! Show everyone your celebration face."
                data-testid="override-body-input" />
            </div>
            <div>
              <Label htmlFor="ov-start" className="text-xs">Starts (UTC)</Label>
              <Input id="ov-start" type="date" value={form.starts_at} onChange={set("starts_at")}
                data-testid="override-start-input" />
            </div>
            <div>
              <Label htmlFor="ov-end" className="text-xs">Ends (UTC, inclusive)</Label>
              <Input id="ov-end" type="date" value={form.ends_at} onChange={set("ends_at")}
                data-testid="override-end-input" />
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <Button onClick={save} disabled={busy}
              className="rounded-full bg-ink text-white hover:bg-[#333] h-10 px-6"
              data-testid="override-save-btn">
              {busy ? "Saving…" : editingId ? "Save changes" : "Schedule override"}
            </Button>
            {editingId && (
              <Button variant="outline" className="rounded-full h-10 px-5"
                onClick={() => { setEditingId(null); setForm(EMPTY); }}
                data-testid="override-cancel-edit-btn">
                Cancel
              </Button>
            )}
          </div>
        </div>

        {/* Overrides list */}
        <div className="mt-8" data-testid="overrides-list">
          <h2 className="font-spectral text-lg text-ink">Scheduled overrides</h2>
          {overrides.length === 0 && (
            <p className="text-sm text-[#6B6B6B] mt-2">
              None yet — the default weekly rotation is running.
            </p>
          )}
          <ul className="mt-3 space-y-3">
            {overrides.map((ov) => {
              const st = statusOf(ov);
              return (
                <li key={ov.id}
                  className="rounded-xl border border-[#E5E5E5] bg-white p-4 flex flex-wrap items-center gap-3"
                  data-testid={`override-row-${ov.id}`}>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[st]}`}>
                    {st}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink text-sm">
                      {ov.title} <span className="font-normal text-[#8A6D00]">{ov.tag}</span>
                    </p>
                    <p className="text-xs text-[#6B6B6B] truncate">{ov.body}</p>
                    <p className="text-[11px] text-[#6B6B6B] mt-0.5">
                      {new Date(ov.starts_at).toLocaleDateString()} → {new Date(ov.ends_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full h-8 px-3"
                    onClick={() => startEdit(ov)} data-testid={`override-edit-${ov.id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button variant="outline" size="sm"
                    className="rounded-full h-8 px-3 text-crimson border-crimson/40 hover:bg-crimson hover:text-white"
                    onClick={() => remove(ov)} data-testid={`override-delete-${ov.id}`}>
                    <Trash size={14} />
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Weekly rotation — editable */}
        <div className="mt-10" data-testid="rotation-reference">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-spectral text-lg text-ink flex items-center gap-2">
              <ChatCircle size={20} weight="duotone" className="text-gold-dark" />
              Weekly rotation
              {rotationCustomized && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-[#FFF7E0] text-[#8A6D00] border-[#EAD98A]">
                  customized
                </span>
              )}
            </h2>
            {rotationCustomized && (
              <Button variant="outline" size="sm" className="rounded-full h-8 px-4 text-xs"
                onClick={resetRotation} data-testid="rotation-reset-btn">
                Reset to defaults
              </Button>
            )}
          </div>
          <p className="text-xs text-[#6B6B6B] mt-1">
            Rotates by ISO week number (new topic every Monday). Click the pencil
            to edit any prompt — changes apply from the next matching week.
          </p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2">
            {rotation.map((p, i) => (
              <li key={i} className="text-xs text-[#6B6B6B] rounded-lg border border-[#EEE] bg-white px-3 py-2"
                data-testid={`rotation-item-${i}`}>
                {rotEditIdx === i ? (
                  <div className="space-y-2">
                    <Input value={rotForm.title} placeholder="Title" className="h-8 text-xs"
                      onChange={(e) => setRotForm((f) => ({ ...f, title: e.target.value }))}
                      data-testid={`rotation-title-input-${i}`} />
                    <Input value={rotForm.tag} placeholder="#Hashtag" className="h-8 text-xs"
                      onChange={(e) => setRotForm((f) => ({ ...f, tag: e.target.value }))}
                      data-testid={`rotation-tag-input-${i}`} />
                    <Textarea value={rotForm.body} rows={2} placeholder="Prompt text" className="text-xs"
                      onChange={(e) => setRotForm((f) => ({ ...f, body: e.target.value }))}
                      data-testid={`rotation-body-input-${i}`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveRotEdit} disabled={rotBusy}
                        className="rounded-full h-7 px-4 text-[11px] bg-ink text-white hover:bg-[#333]"
                        data-testid={`rotation-save-${i}`}>
                        {rotBusy ? "Saving…" : "Save"}
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full h-7 px-3 text-[11px]"
                        onClick={() => setRotEditIdx(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-ink">{p.title}</span>{" "}
                      <span className="text-[#8A6D00]">{p.tag}</span>
                      <span className="block mt-0.5">{p.body}</span>
                    </div>
                    <button type="button" onClick={() => startRotEdit(i, p)}
                      className="text-[#6B6B6B] hover:text-ink shrink-0 mt-0.5"
                      data-testid={`rotation-edit-${i}`} aria-label={`Edit ${p.title}`}>
                      <PencilSimple size={14} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </AppShell>
  );
}
